import React from 'react';
import { BuiltLogoIcon } from './BuiltLogoIcon';
import { getBuildLevel, getBuildLevelLabel } from '../../utils/buildLevel';
import { VEHICLE_BADGE_SHELL } from './vehicleBadgeStyles';

type DiamondScoreBadgeProps = {
  modCount: number;
  size?: 'sm' | 'md';
  variant?: 'pill' | 'plain';
  className?: string;
};

/** Pip size — slightly oversized so the diagonal wrench reads clearly. */
function iconSizeForBadge(size: 'sm' | 'md'): string {
  return size === 'md' ? 'w-[1.15rem] h-[1.15rem]' : 'w-4 h-4';
}

/**
 * Build score: 1–5 diagonal wrenches (logo lean + metal finish).
 * Pill uses charcoal chrome with an orange edge so it sits with the new brand mark.
 */
export const DiamondScoreBadge: React.FC<DiamondScoreBadgeProps> = ({
  modCount,
  size = 'sm',
  variant = 'pill',
  className = '',
}) => {
  const level = getBuildLevel(modCount);
  const iconClass = iconSizeForBadge(size);
  const label = getBuildLevelLabel(modCount);
  const gap = size === 'md' ? 'gap-1' : 'gap-0.5';

  const wrenches = (
    <span className={`inline-flex items-center ${gap}`} aria-hidden>
      {Array.from({ length: level.wrenches }, (_, index) => (
        <BuiltLogoIcon
          key={index}
          tone="metal"
          className={`${iconClass} shrink-0 block drop-shadow-[0_1px_1px_rgba(0,0,0,0.55)]`}
        />
      ))}
    </span>
  );

  if (variant === 'plain') {
    return (
      <span
        className={`${VEHICLE_BADGE_SHELL} ${gap} ${className}`}
        title={label}
        aria-label={label}
        data-testid={`build-score-${level.wrenches}`}
      >
        {wrenches}
      </span>
    );
  }

  return (
    <div
      className={[
        VEHICLE_BADGE_SHELL,
        // Charcoal plate + orange brand edge; soft tier wash underneath for rarity cue
        'bg-gradient-to-br from-[#0a0a0a] via-[#141414] to-[#0a0a0a]',
        'border border-orange-500/35',
        'ring-1 ring-inset ring-white/5',
        className,
      ].join(' ')}
      title={label}
      aria-label={label}
      data-testid={`build-score-${level.wrenches}`}
    >
      {/* Quiet tier tint behind wrenches — keeps rarity color without fighting the metal pips */}
      <span
        className={`pointer-events-none absolute inset-0 rounded-full bg-gradient-to-r ${level.color} opacity-[0.18]`}
        aria-hidden
      />
      <span className="relative z-[1] inline-flex items-center">{wrenches}</span>
    </div>
  );
};

export default DiamondScoreBadge;
